// The failures a tool's capabilities report, which the platform uses too:
// src/domain/errors.ts re-exports them.

import { Schema } from "effect";

/**
 * Why a call to a service, or to a binding the Worker needs, failed. The
 * reason picks a failure's HTTP status and its public detail. See Design >
 * Errors, "A failure carries its context as fields" and "An outage is a 503".
 * - Unavailable: no answer for now, through a timeout, a network failure, a
 *   429 or a 5xx. Retryable.
 * - Refused: a definite refusal, such as a 4xx other than 429.
 * - InvalidResponse: an answer the platform can't use, such as a body that
 *   isn't JSON.
 * - NotConfigured: a binding or a secret this deployment lacks.
 * - Conflict: a write that met a concurrent change or a constraint.
 */
export const FailureReason = Schema.Literals(["Unavailable", "Refused", "InvalidResponse", "NotConfigured", "Conflict"]);
/** One of FailureReason's values: Unavailable, Refused, InvalidResponse, NotConfigured or Conflict. */
export type FailureReason = typeof FailureReason.Type;

/**
 * The fields of a failure of a service the platform calls. `message` holds
 * the cause, for the log line only; `detail`, when set, is text the platform
 * wrote, which a response may carry instead of the generic one. See Design >
 * Errors, "Redaction".
 */
export const serviceFailureFields = {
  /** The call that failed, such as `get` or `query`. */
  operation: Schema.String,
  reason: FailureReason,
  /** Whether the same call might succeed later. */
  retryable: Schema.Boolean,
  /**
   * The cause. In platform code it's for the log line only, and no response
   * carries it. A tool's failure is different: its message reaches the
   * consumer (see ToolError).
   */
  message: Schema.String,
  /** Text the platform wrote, which a response may carry instead of the generic one. Never empty. */
  detail: Schema.optionalKey(Schema.NonEmptyString),
  /** The HTTP status of the service's failing response, when it answered. */
  status: Schema.optionalKey(Schema.Number),
  /** The service's Retry-After, in seconds, when it sent one. */
  retryAfterSeconds: Schema.optionalKey(Schema.Number),
};

/**
 * A failed WarmHub read: the failure of every WarmHubReader method. A tool
 * may recover from it, such as with `Effect.catchTag("WarmHubError", ...)`,
 * or let it fail the tool, which the tool sandbox reports as a ToolFailure
 * that keeps `message` and `retryable`.
 *
 * In a tool sandbox, the error crosses RPC as `operation`, `message` and
 * `retryable` alone, and `reason` is Unavailable when it's retryable and
 * Refused otherwise. `code`, `status`, `detail`, `retryAfterSeconds` and
 * `fromWarmHub` are absent there; the error's code, when it has one, ends the
 * message in parentheses, such as "(COUNT_TRUNCATED)" for a count that
 * stopped at WarmHub's window. A tool's failure message reaches the consumer
 * (see ToolError). See Design > Toolsets and tools, "WarmHubReader".
 */
export class WarmHubError extends Schema.TaggedError<WarmHubError>()("WarmHubError", {
  ...serviceFailureFields,
  /** The SDK's error code, such as VALIDATION_ERROR, UNAUTHENTICATED or COUNT_TRUNCATED. */
  code: Schema.optionalKey(Schema.String),
  /**
   * True when the error came out of an SDK call to WarmHub, or the call ran
   * out of time; false or absent when the platform made it (an invalid shape
   * ref, a truncated count).
   */
  fromWarmHub: Schema.optionalKey(Schema.Boolean),
  /** True when the call to WarmHub ran out of time. */
  timedOut: Schema.optionalKey(Schema.Boolean),
}) {}

/**
 * A thrown value's message, or `fallback`, by default the value itself, when
 * it has none. It never throws, whatever was thrown.
 */
export const errorMessage = (e: unknown, fallback: unknown = e): string =>
  String(typeof e === "object" && e !== null && "message" in e && e.message != null ? e.message : fallback);
